/**
 * RAM — Change OD Limit (Wave 10 A5 BLOCKER).
 *
 * POST /v3/maint/acct/change-od-limit
 *
 * Threshold-routed: increases ≥ $500 require Supervisor approval per the
 * MaintActionRoutingRules `over-threshold` row. The backend computes the
 * approval scenario; this UI surfaces the result so the FSR understands
 * what happened.
 */
import { useState } from 'react';
import { Banner, Button, Pill } from '@/components/primitives';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useMaintActionSubmit } from '@/modules/Maint/useMaintActionSubmit';
import type { RamTarget } from '../types';

export interface ChangeOdLimitProps {
  target: RamTarget;
  onDone: () => void;
  onCancel: () => void;
}

export default function ChangeOdLimit({
  target,
  onDone,
  onCancel,
}: ChangeOdLimitProps): JSX.Element {
  const client = useApiClient();
  const { submit, submitting, error } = useMaintActionSubmit();
  const [newLimit, setNewLimit] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  const [done, setDone] = useState<boolean>(false);
  const [approvalNote, setApprovalNote] = useState<string | null>(null);

  const numericLimit = Number.parseFloat(newLimit);
  const canSave = !submitting && !Number.isNaN(numericLimit) && numericLimit >= 0 && Boolean(target.acctId);

  const handleSave = async (): Promise<void> => {
    if (!canSave || !target.acctId) return;
    const result = await submit(
      (b) => client.retail.maintActions.submitChangeOdLimit(b),
      {
        accountNumber: target.acctId,
        newLimitAmount: numericLimit,
        reason: reason.trim() || undefined,
      },
    );
    if (!result.success) return;
    if (result.data?.approvalRequired) {
      setApprovalNote(
        `Submitted — awaiting ${result.data.approverRole ?? 'approver'} approval (scenario: ${result.data.approvalScenario ?? 'default'}).`,
      );
    } else if (result.data?.testModeSkipped) {
      setApprovalNote('Test-mode — jXchange call skipped. Audit row recorded.');
    } else {
      setApprovalNote(null);
    }
    setDone(true);
  };

  if (done) {
    return (
      <div className="ram-wizard">
        <Banner tone="ok">
          OD limit submitted for account {target.acctId}.
        </Banner>
        {approvalNote && <Banner tone="info">{approvalNote}</Banner>}
        <div className="ram-wizard__actions">
          <Button variant="primary" onClick={onDone}>Done</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="ram-wizard">
      <div className="ram-wizard__header">
        <h2 className="ram-wizard__title">Change OD limit</h2>
        <Pill variant="info">CIF {target.cifId}</Pill>
        {target.acctId && <Pill variant="muted">Account {target.acctId}</Pill>}
      </div>

      <Banner tone="info">
        Increases of $500 or more route to a Supervisor for approval.
      </Banner>

      <div className="ram-wizard__fields">
        <label className="ram-search__label">
          New OD limit ($)
          <input
            className="ram-input"
            value={newLimit}
            onChange={(e) => setNewLimit(e.target.value.replace(/[^0-9.]/g, ''))}
            inputMode="decimal"
            placeholder="0.00"
          />
        </label>
        <label className="ram-search__label">
          Reason
          <textarea
            className="ram-input"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            maxLength={512}
          />
        </label>
      </div>

      {error && <Banner tone="err">{error}</Banner>}

      <div className="ram-wizard__actions">
        <Button variant="ghost" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={!canSave}>
          {submitting ? 'Submitting…' : 'Submit OD limit change'}
        </Button>
      </div>
    </div>
  );
}
